import type { FC } from 'react';
import { useCallback } from 'react';
import { Box, Flex, VStack } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { useCustomerRegistration } from '../../../../bff-api/customers/useCustomerRegistration';
import { useEmarsysSendAccountCreatedEmail } from '../../../../bff-api/emarsys/useEmarsysSendAccountCreatedEmail';
import { BasicError } from '../../../../components/basic-error/BasicError';
import { ConditionalRender } from '../../../../components/conditionally-render/ConditionalRender';
import { Field } from '../../../../components/field';
import { SoftLink } from '../../../../components/soft-link/SoftLink';
import { TermsConditionsPrivacyPolicyNew } from '../../../../components/terms-conditions-privacy-policy/TermsConditionsPrivacyPolicyNew';
import { redirectWithPost } from '../../../../components/utils';
import { useGTM, useNavigation, useSearchParams } from '../../../../hooks';
import {
    RecaptchaWorkflowState,
    useRecaptchaWorkflow,
} from '../../../../recaptcha/hooks/useRecaptchaWorkflow';
import { RecaptchaAction, RecaptchaFeature } from '../../../../recaptcha/recaptcha';
import { useRecaptchaVerify } from '../../../../recaptcha/useRecaptchaVerify';
import { Button, Header, Text } from '../../../../ui-components';
import { apiErrorBody } from '../../../../utils/api-error-body';
import { PageUrl } from '../../../utils/page-url';
import { PasswordRequirements } from '../password-requirements/PasswordRequirements';
import { useRegistrationFields } from './useRegistrationFields';
import type { RegisterCustomerDTO } from '../../model/RegisterCustomerDTO';

export const Registration: FC = () => {
    const intl = useIntl();
    const mutation = useCustomerRegistration();
    const navigate = useNavigation();
    const emarsysSendAccountCreatedEmail = useEmarsysSendAccountCreatedEmail();
    const recaptchaVerify = useRecaptchaVerify();
    const form = useForm<RegisterCustomerDTO>({
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            signUpNewsletter: true,
            password: '',
        },
    });

    const fields = useRegistrationFields(form);
    const query = useSearchParams();
    const { onUserRegistration } = useGTM();

    const {
        isRecaptchaEnabled,
        getRecaptchaPayload,
        handleApiError,
        handleApiSuccess,
        V2Widget,
        errorMessage: recaptchaError,
        isBlocked,
        state,
    } = useRecaptchaWorkflow({
        feature: RecaptchaFeature.ACCOUNT_REGISTRATION,
        action: RecaptchaAction.REGISTER,
        autoSubmitOnV2: true,
        onV2Complete: async () => {
            await submitFormHandler(form.getValues());
        },
    });

    const submitFormHandler = useCallback(
        async (data: RegisterCustomerDTO): Promise<void> => {
            try {
                if (isRecaptchaEnabled) {
                    const recaptchaPayload = await getRecaptchaPayload();
                    if (!recaptchaPayload) {
                        throw new Error(
                            `Failed to get reCAPTCHA payload for ${RecaptchaAction.REGISTER}`,
                        );
                    }
                    await recaptchaVerify.mutateAsync({
                        recaptcha: {
                            ...recaptchaPayload,
                            feature: RecaptchaFeature.ACCOUNT_REGISTRATION,
                        },
                    });
                }

                await mutation.mutateAsync(data);

                handleApiSuccess();

                onUserRegistration(data);

                await emarsysSendAccountCreatedEmail.mutateAsync(data);

                const redirectUrl = query.get('redirect-url');
                // The following is an edge case, should be removed after the migration of the gift registry
                if (redirectUrl && redirectUrl.endsWith(PageUrl.GIFT_REGISTRY_SUBMIT)) {
                    redirectWithPost(redirectUrl, { dwfrm_giftregistry_create: 'New+Registry' });
                    return;
                }
                navigate(redirectUrl ?? PageUrl.ACCOUNT, 'replace');
            } catch (err) {
                const wasRecaptchaError = handleApiError(err);

                if (wasRecaptchaError) {
                    return;
                }

                if (err instanceof Error) {
                    const { message } = err;
                    const { response } = err as unknown as { response?: Response };
                    const body = await apiErrorBody(err);
                    const apiErrorDetails = body.detail ?? '';
                    const enrichedMessage = [message, apiErrorDetails].join(' -- ');
                    if (/login is already in use/i.test(enrichedMessage)) {
                        form.setError('global', {
                            type: 'manual',
                            message: messages.loginAlreadyInUse.id,
                        });
                        form.setError('email', {
                            type: 'manual',
                            message: intl.formatMessage(messages.loginAlreadyInUse),
                        });
                    } else if (/Password does not match constraints/i.test(enrichedMessage)) {
                        form.setError('global', {
                            type: 'manual',
                            message: messages.passwordConstraintsError.id,
                        });
                        form.setError('password', {
                            type: 'manual',
                            message: intl.formatMessage(messages.passwordConstraintsError),
                        });
                    } else {
                        const errorMessage = intl.formatMessage(messages.generalError);
                        if (response) {
                            console.error(errorMessage, body);
                        }
                        form.setError('global', {
                            type: 'manual',
                            message: errorMessage,
                        });
                    }
                }
            }
        },
        [
            mutation,
            emarsysSendAccountCreatedEmail,
            form,
            intl,
            navigate,
            query,
            isRecaptchaEnabled,
            getRecaptchaPayload,
            recaptchaVerify,
            handleApiError,
            handleApiSuccess,
            onUserRegistration,
        ],
    );

    const password = form.watch('password');

    return (
        <Box
            width={{ base: '100%', sm: '448px', md: '100%', xl: '640px' }}
            maxWidth={{ md: '640px' }}
            margin={{ base: '2rem 1rem', sm: '2rem' }}
        >
            <VStack spacing={6} width="100%">
                <VStack spacing={2} width="100%" align="stretch">
                    <Header
                        as="h3"
                        variant="header3"
                        id="account-registration-heading"
                        color="slt-pepper.1000"
                    >
                        <FormattedMessage
                            id="account.registration.heading"
                            defaultMessage="Create an Account"
                        />
                    </Header>

                    <Text as="span" id="account-registration-subheading">
                        <FormattedMessage
                            id="account.registration.subheading"
                            defaultMessage="Join our culinary community and unlock expert cooking techniques, exclusive recipes, and premium kitchenware"
                        />
                    </Text>
                </VStack>

                <form
                    onSubmit={form.handleSubmit(submitFormHandler)}
                    data-testid="account-registration-form"
                    className="w-full"
                >
                    <VStack spacing={6} width="100%">
                        {(form.formState.errors.global || recaptchaError) && (
                            <BasicError
                                options={{
                                    alertDataTestId: 'account-registration-error-message',
                                    errorMessageDataTestId: 'account-registration-error-text',
                                    showOutline: false,
                                    iconVerticalAlign: 'top',
                                }}
                            >
                                {recaptchaError && (
                                    <Text color="slt-red-error">{recaptchaError}</Text>
                                )}
                                {!recaptchaError && form.formState.errors.global && (
                                    <FormattedMessage id={form.formState.errors.global.message} />
                                )}
                            </BasicError>
                        )}
                        <VStack spacing={6} width="100%" align="stretch">
                            <Flex direction={{ base: 'column', sm: 'row' }} gap={6} width="100%">
                                <Box flex="1">
                                    <Field {...fields.firstName} />
                                </Box>
                                <Box flex="1">
                                    <Field {...fields.lastName} />
                                </Box>
                            </Flex>
                            <VStack spacing={4} align="stretch">
                                <Field {...fields.email} />
                                <Field {...fields.signUpNewsletter} />
                            </VStack>
                            <VStack spacing={2} align="stretch">
                                <Field {...fields.password} />
                                <Text size="small" style="bold">
                                    <FormattedMessage
                                        id="account.create_account.password_requirements"
                                        defaultMessage="Password must contain the following:"
                                    />
                                </Text>
                                <PasswordRequirements value={password ?? ''} />
                            </VStack>
                        </VStack>
                        <VStack spacing={2} width="100%" align="center">
                            <ConditionalRender
                                visibility={state === RecaptchaWorkflowState.V2_REQUIRED}
                            >
                                {V2Widget}
                            </ConditionalRender>
                            <Button
                                kind="submit"
                                variant="primary-black"
                                onClick={(): void => form.clearErrors('global')}
                                isLoading={form.formState.isSubmitting || recaptchaVerify.isLoading}
                                isDisabled={isBlocked || recaptchaVerify.isLoading}
                                id="account-registration-submit-button"
                                wide
                            >
                                <FormattedMessage
                                    id="account.registration.button.create_account"
                                    defaultMessage="Create Account"
                                />
                            </Button>

                            <TermsConditionsPrivacyPolicyNew prefix="account-registration">
                                <FormattedMessage
                                    id="account.registration.disclaimer"
                                    defaultMessage="By creating an account, you are agreeing to our"
                                />
                            </TermsConditionsPrivacyPolicyNew>
                        </VStack>
                    </VStack>
                </form>

                <Flex width="100%" justifyContent="center" gap={1}>
                    <Text id="account-registration-signin-text">
                        <FormattedMessage
                            id="account.registration.already_have_account"
                            defaultMessage="Already have an account?"
                        />
                    </Text>
                    <Button variant="tertiary" padding="0px">
                        <SoftLink
                            to={PageUrl.ACCOUNT_LOGIN}
                            data-testid="account-registration-signin-link"
                        >
                            <Text as="span" style="bold">
                                <FormattedMessage
                                    id="account.registration.sign_in_prompt"
                                    defaultMessage="Sign In"
                                />
                            </Text>
                        </SoftLink>
                    </Button>
                </Flex>
            </VStack>
        </Box>
    );
};

const messages = defineMessages({
    loginAlreadyInUse: {
        id: 'account.registration.error.already_in_use',
        defaultMessage:
            'You already have an account with this email. Sign in or create a new account with another email.',
    },
    passwordConstraintsError: {
        id: 'account.registration.error.server_constraints',
        defaultMessage: 'Password does not match constraints.',
    },
    generalError: {
        id: 'account.registration.error.unknown_error',
        defaultMessage: 'Error registering the user, please contact support.',
    },
});
